import React, {useMemo} from "react";
import {AsYouType, CountryCode, parsePhoneNumberWithError} from "libphonenumber-js";
import styles from "./styles/PhoneNumber.module.scss"

interface FormattingResult {
    formattedNumber: string;
    unformattedNumber: string;
    isValid: boolean;
    isPartiallyValid: boolean;
    extension?: string;
    error?: string;
}

interface PhoneFormatResult {
    formattedNumber: string;
    unformattedNumber: string;
    error?: string;
}

/**
 * Maps country codes to country identifiers for libphonenumber-js
 */
function getCountryForCode(countryCode: string): string {
    const countryMap: Record<string, string> = {
        "1": "US",
        "44": "GB",
        "33": "FR",
        "49": "DE",
        "39": "IT",
        "34": "ES",
        "81": "JP",
        "86": "CN",
        "91": "IN",
        "7": "RU",
    };
    return countryMap[countryCode] || "US";
}

/**
 * Extracts extension from a phone number string
 * Supports formats like: x123, ext 123, ext. 123, extension 123
 */
function extractExtension(phoneStr: string): {
    number: string;
    extension?: string;
} {
    const extensionPatterns = [
        /\s*(?:ext|extension|x)\.?\s*(\d+)$/i, // Matches ext/x/extension followed by numbers
        /\s*[,;]\s*(\d+)$/, // Matches numbers after comma or semicolon
    ];

    for (const pattern of extensionPatterns) {
        const match = phoneStr.match(pattern);
        if (match) {
            return {
                number: phoneStr.slice(0, match.index).trim(),
                extension: match[1],
            };
        }
    }

    return { number: phoneStr };
}

/**
 * Handles bigint phone numbers from database and extracts extensions
 * For US numbers: 11 digits starting with 1 = US number, >11 digits = extension
 */
function processBigIntPhoneNumber(
    numberInput: bigint | number | string
): { number: string; extension?: string } {
    const numberStr = String(numberInput);

    // If it's already formatted with + or has non-digits, treat as string
    if (numberStr.includes("+") || /[^\d]/.test(numberStr)) {
        return extractExtension(numberStr);
    }

    // For pure digit strings from bigint
    const digits = numberStr.replace(/\D/g, "");

    // Handle US numbers with country code (11 digits starting with 1)
    if (digits.length === 11 && digits.startsWith("1")) {
        return { number: digits };
    }

    // For numbers longer than 11 digits, assume extension
    if (digits.length > 11) {
        // If starts with 1, it's likely US number + extension
        if (digits.startsWith("1")) {
            const baseNumber = digits.slice(0, 11);
            const extension = digits.slice(11);
            return {
                number: baseNumber,
                extension: extension,
            };
        }
        // For non-US numbers, assume 10 digit base + extension
        const baseNumber = digits.slice(0, 10);
        const extension = digits.slice(10);
        return {
            number: baseNumber,
            extension: extension,
        };
    }

    return { number: digits };
}

/**
 * Extracts the pure integer value from a phone number string
 * Removes all non-digit characters and returns the numeric value
 * Returns bigint for numbers longer than 15 digits to handle large phone numbers safely
 */
export function getPhoneNumberInteger(
    phoneNumber: string | undefined | null
): number | bigint | null {
    // Handle null/undefined/empty cases
    if (!phoneNumber || typeof phoneNumber !== "string") {
        return null;
    }

    // Extract only digits
    const digitsOnly = phoneNumber.replace(/\D/g, "");

    // Return null if no digits found
    if (digitsOnly.length === 0) {
        return null;
    }

    // Use bigint for very long numbers to avoid precision loss
    // JavaScript's Number.MAX_SAFE_INTEGER is 2^53-1 (15-16 digits)
    if (digitsOnly.length > 15) {
        return BigInt(digitsOnly);
    }

    return parseInt(digitsOnly, 10);
}

/**
 * Formats a phone number with improved error handling and bigint support.
 * If the number starts with "+", it's treated as international.
 * Otherwise, it's assumed to be a US number and formatted nationally.
 */
export function formatPhoneNumber(
    numberInput: bigint | number | string | undefined | null
): PhoneFormatResult {
    const emptyReturn: PhoneFormatResult = {
        formattedNumber: "",
        unformattedNumber: "",
    };

    if (
        numberInput === undefined ||
        numberInput === null ||
        numberInput === 0 ||
        numberInput === ""
    ) {
        return emptyReturn;
    }

    try {
        const numberStr = String(numberInput);
        const { number, extension } = processBigIntPhoneNumber(numberInput);

        let parsedNumber;
        try {
            // 'US' is the default country if no international prefix is present
            parsedNumber = parsePhoneNumberWithError(number, "US");
        } catch (parseError) {
            return {
                ...emptyReturn,
                error:
                    parseError instanceof Error
                        ? parseError.message
                        : "Invalid phone number format",
            };
        }

        const e164Number = parsedNumber.number;
        let display: string;

        if (numberStr.startsWith("+")) {
            display = parsedNumber.format("INTERNATIONAL");
        } else {
            display = parsedNumber.format("NATIONAL");
        }

        if (extension) {
            display += ` ext. ${extension}`;
            return {
                formattedNumber: display,
                unformattedNumber: `${e164Number} x${extension}`,
            };
        }

        return {
            formattedNumber: display,
            unformattedNumber: e164Number,
        };
    } catch (error) {
        console.warn("Phone number formatting failed:", error);
        return {
            ...emptyReturn,
            error:
                error instanceof Error ? error.message : "Phone number formatting error",
        };
    }
}

/**
 * Formats and validates a phone number as it's being typed.
 * Returns formatting information and validation state.
 */
export function formatPhoneNumberAsYouType(
    input: string,
    countryCode: string = "1"
): FormattingResult {
    const emptyResult: FormattingResult = {
        formattedNumber: "",
        unformattedNumber: "",
        isValid: false,
        isPartiallyValid: false,
    };

    if (!input.trim()) {
        return emptyResult;
    }

    try {
        // Extract extension if present
        const { number, extension } = extractExtension(input);

        // Remove all non-digit characters from the main number
        const digitsOnly = number.replace(/\D/g, "");

        if (digitsOnly.length === 0) {
            return emptyResult;
        }

        // Initialize formatter with country
        const country = getCountryForCode(countryCode);
        const formatter = new AsYouType(country as CountryCode);
        const formattedNumber = formatter.input(digitsOnly);

        // Get the complete number with country code
        const fullNumber = `+${countryCode}${digitsOnly}`;

        // Determine validity states
        const isPartiallyValid = digitsOnly.length > 0 && digitsOnly.length <= 15; // International max
        let isValid = false;

        try {
            const parsed = parsePhoneNumberWithError(fullNumber);
            isValid = parsed.isValid();
        } catch (parseError) {
            // If parsing fails, number is not valid
            isValid = false;
        }

        const result: FormattingResult = {
            formattedNumber: extension
                ? `${formattedNumber} x${extension}`
                : formattedNumber,
            unformattedNumber: extension ? `${fullNumber} x${extension}` : fullNumber,
            isValid,
            isPartiallyValid,
        };

        if (extension) {
            result.extension = extension;
        }

        return result;
    } catch (error) {
        return {
            ...emptyResult,
            error:
                error instanceof Error
                    ? error.message
                    : "Phone number formatting error",
        };
    }
}

interface PhoneNumberProps
    extends Omit<React.HTMLAttributes<HTMLElement>, "children"> {
    number?: bigint | number | string;
    isLinked?: boolean;
    description?: string;
    ariaLabel?: string;
    showError?: boolean;
}

const PhoneNumber: React.FC<PhoneNumberProps> = ({
                                                     isLinked = false,
                                                     number,
                                                     className,
                                                     description,
                                                     ariaLabel,
                                                     showError = false,
                                                     ...validDomAttributes
                                                 }) => {

    const { formattedNumber, unformattedNumber, error } = useMemo(() =>
        formatPhoneNumber(number), [number]);

    // Don't render anything if there's no formatted number
    if (!formattedNumber && !error) {
        return null;
    }

    const phoneElement = isLinked ? (
        <a
            aria-label={ariaLabel || `telephone number ${formattedNumber}`}
            href={`tel:${unformattedNumber}`}
            className={`phone-number-link ${className || ""}`.trim()}
            {...validDomAttributes}
        >
            {formattedNumber}
        </a>
    ) : (
        <span
            aria-label={ariaLabel || formattedNumber}
            className={`phone-number ${className || ""}`.trim()}
            {...validDomAttributes}
        >
      {formattedNumber}
    </span>
    );

    return (
        <div className="phone-number-container">
            {formattedNumber && phoneElement}
            {description && (
                <span className={styles.phoneNumberDescription}> {description}</span>
            )}
            {showError && error && (
                <span className="phone-number-error" role="alert">
          {error}
        </span>
            )}
        </div>
    );
};

export default PhoneNumber;
